import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * Documents API service
 */
export const DocumentApi = {
  /**
   * Generic (admin/global) documents listing with optional filters
   */
  getDocuments: async (options = {}) => {
    const {
      page = 0,
      size = 20,
      sort = "createdAt,desc",
      name,
      documentTypeId,
      endeavorId,
      statusId
    } = options;

    const qp = new URLSearchParams({
      page: String(page),
      size: String(size),
      sort
    });

    if (name) qp.set('name', name);
    if (documentTypeId) qp.set('documentTypeId', documentTypeId);
    if (endeavorId) qp.set('endeavorId', endeavorId);
    if (statusId) qp.set('statusId', statusId);

    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents?${qp.toString()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
  },

  /**
   * User-scoped documents with backend-supported filters
   * Supported filters: name, documentTypeId, endeavorId, statusId, excludeDocumentTypeIds
   */
  getDocumentsByUser: async (userId, options = {}, fetchOpts = {}) => {
    let {
      page = 0,
      size = 20,
      sort = "createdAt,desc",      // can be string OR array of strings
      name,
      documentTypeId,
      endeavorId,
      statusId,
      statusIds,
      excludeDocumentTypeIds,
      ...rest
    } = options;

    const qp = new URLSearchParams({
      page: String(page),
      size: String(size)
    });

    // Support multi-sort: sort may be array or single string
    if (Array.isArray(sort)) {
      sort.forEach(s => s && qp.append('sort', s));
    } else if (sort) {
      qp.append('sort', sort);
    }

    if (name) qp.set('name', name);
    if (documentTypeId) qp.set('documentTypeId', documentTypeId);
    if (endeavorId) qp.set('endeavorId', endeavorId);
    if (statusId) qp.set('statusId', statusId);
    
    // Support multiple status IDs
    if (statusIds && Array.isArray(statusIds)) {
      statusIds.forEach(id => qp.append('statusIds', id));
    }

    // Support excluding multiple document type IDs (audio files, etc.)
    if (excludeDocumentTypeIds && Array.isArray(excludeDocumentTypeIds)) {
      excludeDocumentTypeIds.forEach(id => qp.append('excludeDocumentTypeIds', id));
    }

    Object.entries(rest).forEach(([k,v])=>{
      if (v !== undefined && v !== null) qp.set(k, String(v));
    });

    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/${userId}/documents?${qp.toString()}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      ...fetchOpts
    });
  },

  getDocument: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/${id}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
  },

  getDocumentsByUserAndType: async (userId, typeId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/${userId}/documents/type/${typeId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
  },

  getDocumentsByType: async (typeId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/type/${typeId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
  },

  getDocumentsByStatus: async (statusId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/status/${statusId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
  },

  getDocumentsByMultipleStatuses: async (statusIds) => {
    const queryParams = new URLSearchParams();
    statusIds.forEach(id => queryParams.append('statusIds', id));
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/status?${queryParams}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
  },

  searchDocumentsByName: async (name) => {
    const queryParams = new URLSearchParams({ name }).toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/search?${queryParams}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
  },

  createDocument: async (data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  updateDocument: async (id, data) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
  },

  deleteDocument: async (id) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/documents/${id}`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' }
    });
  },

  // Document Status APIs
  getDocumentStatuses: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/document-statuses`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
  },

  getDocumentStatusByName: async (name) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/document-statuses/name/${encodeURIComponent(name)}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
  }
};